import { AppIcon } from '@/components/ui/app-icon';
import { AgentCard } from '@/components/agent-chat/agent-card.component';
import {
  CardFooter,
  CardLinkButton,
  CardMeta,
} from '@/components/agent-chat/card-parts.component';
import { CompanyLogo } from '@/components/company-logo.component';
import { iconBuilding, iconChevronDown } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { useState, type ReactNode } from 'react';
import { BRAND } from '@/lib/brand';

/**
 * The company profile Clu saved from its research, in the agent card's
 * shell: the company and its one-liner, then two sections that open in
 * place. Ideal customer profile lists what defines a fit and how many
 * companies and people in Cleanlist match; Buyer personas lists who buys
 * and what each one cares about. Edit opens the profile to change it, and
 * Open in Search opens its audience in Find People.
 */

type SectionId = 'icp' | 'personas';

interface Pair {
  label: string;
  value: string;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Label/value pairs from [{label, value}] objects, or bare strings. */
function pairs(value: unknown, keys: [string, string]): Pair[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item === 'string' && item.trim()) {
      return [{ label: item.trim(), value: '' }];
    }
    if (item && typeof item === 'object') {
      const record = item as Record<string, unknown>;
      const label = text(record[keys[0]]);
      if (label) return [{ label, value: text(record[keys[1]]) }];
    }
    return [];
  });
}

function domainOf(data: Record<string, unknown>): string {
  const raw = text(data.domain) || text(data.website_url);
  return raw
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '');
}

function Section({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className='border-t border-border'>
      <button
        type='button'
        onClick={onToggle}
        aria-expanded={open}
        className='flex h-[36px] w-full items-center justify-between px-[12px] text-left text-[12px] leading-[16px] font-medium text-foreground transition-colors hover:bg-muted/40'
      >
        {title}
        <AppIcon
          icon={iconChevronDown}
          className={cn(
            'text-muted-foreground transition-transform duration-200',
            open && 'rotate-180'
          )}
          style={{ fontSize: 10 }}
        />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className='overflow-hidden'
          >
            <div className='px-[12px] pb-[12px]'>{children}</div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/** Two columns, no rules: a label and its value. */
function PairList({
  items,
  emphasis = 'value',
}: {
  items: Pair[];
  /** Which column carries the weight: ICP values, persona titles. */
  emphasis?: 'label' | 'value';
}) {
  return (
    <dl className='grid grid-cols-[200px_1fr] gap-x-[24px] gap-y-[8px] text-[12px] leading-[16px]'>
      {items.map((item) => (
        <div key={item.label} className='contents'>
          <dt
            className={
              emphasis === 'label'
                ? 'font-medium text-foreground'
                : 'text-muted-foreground'
            }
          >
            {item.label}
          </dt>
          <dd
            className={
              emphasis === 'label' ? 'text-muted-foreground' : 'text-foreground'
            }
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function CompanyProfileCard({
  result,
  onEdit,
  onOpenSearch,
  searchRefusal,
}: {
  result: unknown;
  onEdit?: () => void;
  onOpenSearch?: () => void;
  /** Why the audience can't open in Search, shown where the button would be. */
  searchRefusal?: string | null;
}) {
  const data = (result ?? {}) as Record<string, unknown>;
  const name = text(data.company_name) || text(data.name) || 'Your company';
  const domain = domainOf(data);
  const summary = text(data.one_liner) || text(data.summary);
  const icpPairs = pairs(data.icp_attributes, ['label', 'value']);
  const icp = text(data.icp);
  const personas = pairs(data.personas, ['title', 'detail']);
  const market = (data.market ?? {}) as Record<string, unknown>;
  const companies = count(market.companies);
  const people = count(market.people);
  // People counted at the first companies only: a floor, said as one.
  const peopleFloor = market.people_is_lower_bound === true;
  // Sections Clu opens itself (`expanded`); closed otherwise.
  const [open, setOpen] = useState<Set<SectionId>>(
    () =>
      new Set(
        (Array.isArray(data.expanded) ? data.expanded : []).filter(
          (id): id is SectionId => id === 'icp' || id === 'personas'
        )
      )
  );
  const toggle = (id: SectionId) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const hasIcp = icpPairs.length > 0 || Boolean(icp);
  const hasSections = hasIcp || personas.length > 0;

  return (
    <AgentCard
      data-result-card
      icon={iconBuilding}
      title='Company profile'
      trailing={
        onEdit ? (
          <span className='pr-[4px]'>
            <CardLinkButton onClick={onEdit}>Edit</CardLinkButton>
          </span>
        ) : null
      }
    >
      <div className='flex flex-col gap-[8px]'>
        <div className='flex items-center gap-[12px]'>
          <CompanyLogo
            domain={domain}
            name={name}
            className='size-[36px] shrink-0 rounded-[8px] bg-background ring-1 ring-border'
          />
          <div className='min-w-0'>
            <p className='truncate text-[14px] leading-[20px] font-medium text-foreground'>
              {name}
            </p>
            {domain ? (
              <CardMeta>
                <a
                  href={`https://${domain}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='underline decoration-border underline-offset-[3px] transition-colors hover:text-foreground'
                >
                  {domain}
                </a>
              </CardMeta>
            ) : null}
          </div>
        </div>
        {summary ? <CardMeta>{summary}</CardMeta> : null}
      </div>
      {hasSections ? (
        // Full width, so each section's rule meets the card's edges.
        <div className='-mx-[12px] flex flex-col'>
          {hasIcp ? (
            <Section
              title='Ideal customer profile'
              open={open.has('icp')}
              onToggle={() => toggle('icp')}
            >
              {icpPairs.length > 0 ? (
                <PairList items={icpPairs} />
              ) : (
                <p className='text-[12px] leading-[16px] text-foreground/80'>
                  {icp}
                </p>
              )}
              {companies !== null || people !== null ? (
                <CardMeta className='mt-[12px]'>
                  {companies !== null ? (
                    <span className='font-medium text-foreground tabular-nums'>
                      {companies.toLocaleString()} companies
                    </span>
                  ) : null}
                  {companies !== null && people !== null ? ' and ' : null}
                  {people !== null ? (
                    <span className='font-medium text-foreground tabular-nums'>
                      {peopleFloor ? 'at least ' : ''}
                      {people.toLocaleString()} people
                    </span>
                  ) : null}{' '}
                  in {BRAND.name} match
                </CardMeta>
              ) : null}
            </Section>
          ) : null}
          {personas.length > 0 ? (
            <Section
              title='Buyer personas'
              open={open.has('personas')}
              onToggle={() => toggle('personas')}
            >
              <PairList items={personas} emphasis='label' />
            </Section>
          ) : null}
        </div>
      ) : null}
      {onOpenSearch ? (
        <CardFooter
          links={
            <CardLinkButton onClick={onOpenSearch}>
              Open in Search
            </CardLinkButton>
          }
        />
      ) : searchRefusal ? (
        // Where Open in Search would be: why the audience can't open there.
        <div role='note'>
          <CardMeta>{searchRefusal}</CardMeta>
        </div>
      ) : null}
    </AgentCard>
  );
}
